<!-- ══════════════════ Pie & Composition builder ══════════════════
     Mirrors the Scatter & Bubble builder: a family card (Group toggle +
     variant picker), a Chart-Data accordion (Category / Value / Parent /
     Color-by drop-zone pickers + capability toggles), then the config-driven
     Format accordions. NO X/Y axes. Renders client-side via the pure
     pie-composition option builder. -->
<div class="wf-section wf-section-plain" *ngIf="active">
  <ng-container *ngTemplateOutlet="datasetPicker"></ng-container>

  <div class="bridge-chart-settings bar-family-card">
    <div class="bridge-settings-heading"><mat-icon>pie_chart</mat-icon><span>{{ host.layoutSectionTitle('pieComposition', 'Pie & composition') }}</span></div>
    <small class="control-hint" *ngIf="host.layoutSectionHint('pieComposition')">{{ host.layoutSectionHint('pieComposition') }}</small>

    <!-- Group segmented toggle — sides are FULLY config-driven
         (pieCompositionModes: label + icon + value); the Variant dropdown
         below lists only the active side's variants. -->
    <div class="control-group" *ngIf="host.fieldEnabled('pieComposition', 'mode')">
      <label>{{ host.fieldLabel('pieComposition', 'mode', 'Group') }}</label>
      <div class="wf-step-mode-toggle wf-sb-mode-toggle" role="group" [attr.aria-label]="host.fieldLabel('pieComposition', 'mode', 'Group')">
        <button type="button" *ngFor="let m of pcModes" [class.is-active]="pcMode === m.value" (click)="setPcMode(m.value)">
          <mat-icon *ngIf="m.icon">{{ m.icon }}</mat-icon> {{ m.label }}
        </button>
      </div>
    </div>

    <!-- Variant — only the active toggle side's variants (Standard / Hierarchy / Progress). -->
    <div class="control-group" *ngIf="host.fieldEnabled('pieComposition', 'variant')">
      <label>{{ host.fieldLabel('pieComposition', 'variant', 'Variant') }}</label>
      <ng-select
        #ssPcVariant
        class="ss-select"
        [class.is-open]="!!ssPcVariant.isOpen?.()"
        appendTo="body"
        ngClass="ss-dropdown-panel"
        [items]="pcVariantItems"
        bindLabel="label"
        bindValue="value"
        [clearable]="false"
        [ngModel]="pcVariant"
        (ngModelChange)="pcVariant = $event; onPieCompositionChange()"
      ></ng-select>
      <small class="control-hint wf-variant-hint" *ngIf="pcVariantHint">{{ pcVariantHint }}</small>
    </div>
  </div>

  <!-- ══ Chart Data accordion — Category / Value / Parent / Color by measure ══ -->
  <div class="bar-options-accordion">
    <div class="dvm-format-group" [class.is-open]="pcDataAccordionOpen">
      <button type="button" class="dvm-format-group-head" (click)="pcDataAccordionOpen = !pcDataAccordionOpen">
        <span>Chart Data</span>
        <mat-icon>{{ pcDataAccordionOpen ? 'expand_less' : 'expand_more' }}</mat-icon>
      </button>
      <div class="dvm-format-group-body" *ngIf="pcDataAccordionOpen">

        <div class="control-row">
          <div class="control-group wf-field-drop-zone" [class.is-required-empty]="!pcCategoryField" (dragover)="host.allowFieldDrop($event)" (drop)="onPcCategoryFieldDrop($event)" title="Drag a column here">
            <label>{{ host.fieldLabel('pieComposition', 'categoryField', 'Category') }} <span class="wf-axis-hint">(required)</span></label>
            <ng-select
              #ssPcCategory class="ss-select" [class.is-open]="!!ssPcCategory.isOpen?.()" appendTo="body" ngClass="ss-dropdown-panel"
              [items]="pieCompositionColumns" [clearable]="true" [searchable]="true"
              [loading]="host.isDrillDatasetColumnsLoading" [disabled]="!host.drillDataset"
              [placeholder]="host.drillDataset ? 'Pick a column' : 'Select a dataset first'"
              [ngModel]="pcCategoryField || null" (ngModelChange)="pcCategoryField = $event || ''; onPieCompositionChange()"
            ></ng-select>
          </div>
          <div class="control-group wf-field-drop-zone" [class.is-required-empty]="!pcValueField" (dragover)="host.allowFieldDrop($event)" (drop)="onPcValueFieldDrop($event)" title="Drag a column here">
            <label>{{ host.fieldLabel('pieComposition', 'valueField', 'Value') }} <span class="wf-axis-hint">— measure</span></label>
            <ng-select
              #ssPcValue class="ss-select" [class.is-open]="!!ssPcValue.isOpen?.()" appendTo="body" ngClass="ss-dropdown-panel"
              [items]="pieCompositionColumns" [clearable]="true" [searchable]="true"
              [loading]="host.isDrillDatasetColumnsLoading" [disabled]="!host.drillDataset"
              [placeholder]="host.drillDataset ? 'Pick a column' : 'Select a dataset first'"
              [ngModel]="pcValueField || null" (ngModelChange)="pcValueField = $event || ''; onPieCompositionChange()"
            ></ng-select>
          </div>
          <div class="control-group" *ngIf="host.fieldEnabled('pieComposition', 'valueAggregation')">
            <label>{{ host.fieldLabel('pieComposition', 'valueAggregation', 'Value aggregation') }}</label>
            <ng-select
              #ssPcValueAgg class="ss-select" [class.is-open]="!!ssPcValueAgg.isOpen?.()" appendTo="body" ngClass="ss-dropdown-panel"
              [items]="host.measureAggregations" bindLabel="label" bindValue="value" [clearable]="true"
              [ngModel]="pcValueAggregation" (ngModelChange)="pcValueAggregation = $event ?? ''; onPieCompositionChange()"
            ></ng-select>
          </div>
        </div>

        <div class="control-row">
          <!-- Parent / outer group — REQUIRED for Hierarchy variants, and for
               Standard/Progress once Multiple Categories is on (drives the
               small-multiples split). -->
          <div class="control-group wf-field-drop-zone" [class.is-required-empty]="pcNeedsParent && !pcParentField" *ngIf="pcNeedsParent" (dragover)="host.allowFieldDrop($event)" (drop)="onPcParentFieldDrop($event)" title="Drag a column here">
            <label>{{ host.fieldLabel('pieComposition', 'parentField', 'Parent / outer group') }} <span class="wf-axis-hint">(required)</span></label>
            <ng-select
              #ssPcParent class="ss-select" [class.is-open]="!!ssPcParent.isOpen?.()" appendTo="body" ngClass="ss-dropdown-panel"
              [items]="pieCompositionColumns" [clearable]="true" [searchable]="true"
              [loading]="host.isDrillDatasetColumnsLoading" [disabled]="!host.drillDataset"
              placeholder="Pick a grouping column"
              [ngModel]="pcParentField || null" (ngModelChange)="pcParentField = $event || ''; onPieCompositionChange()"
            ></ng-select>
          </div>
          <!-- Color by measure — optional, shown once the toggle is on. -->
          <div class="control-group wf-field-drop-zone" [class.is-required-empty]="pcColorByMeasure && !pcColorField" *ngIf="pcColorByMeasure" (dragover)="host.allowFieldDrop($event)" (drop)="onPcColorFieldDrop($event)" title="Drag a column here">
            <label>{{ host.fieldLabel('pieComposition', 'colorField', 'Color by') }} <span class="wf-axis-hint">(measure)</span></label>
            <ng-select
              #ssPcColor class="ss-select" [class.is-open]="!!ssPcColor.isOpen?.()" appendTo="body" ngClass="ss-dropdown-panel"
              [items]="pieCompositionColumns" [clearable]="true" [searchable]="true"
              [loading]="host.isDrillDatasetColumnsLoading" [disabled]="!host.drillDataset"
              placeholder="Measure driving the color gradient"
              [ngModel]="pcColorField || null" (ngModelChange)="pcColorField = $event || ''; onPieCompositionChange()"
            ></ng-select>
          </div>
        </div>

        <!-- Capabilities — Multiple Categories / Drill Down / Color by Measure
             (Legend lives in the Format accordion below, like every other chart). -->
        <div class="control-row wf-pc-capabilities">
          <label class="dvm-format-toggle" *ngIf="host.fieldEnabled('pieComposition', 'multipleCategories') && !pcVariantUsesParent">
            <input type="checkbox" [ngModel]="pcMultipleCategories" (ngModelChange)="pcMultipleCategories = $event; onPieCompositionChange()" />
            {{ host.fieldLabel('pieComposition', 'multipleCategories', 'Multiple categories (one chart per parent)') }}
          </label>
          <label class="dvm-format-toggle" *ngIf="host.fieldEnabled('pieComposition', 'drillDownEnabled') && pcVariantUsesParent">
            <input type="checkbox" [ngModel]="pcDrillDownEnabled" (ngModelChange)="pcDrillDownEnabled = $event; onPieCompositionChange()" />
            {{ host.fieldLabel('pieComposition', 'drillDownEnabled', 'Enable click-to-drill') }}
          </label>
          <label class="dvm-format-toggle" *ngIf="host.fieldEnabled('pieComposition', 'colorByMeasure') && !pcVariantIsGauge">
            <input type="checkbox" [ngModel]="pcColorByMeasure" (ngModelChange)="pcColorByMeasure = $event; onPieCompositionChange()" />
            {{ host.fieldLabel('pieComposition', 'colorByMeasure', 'Color by measure') }}
          </label>
        </div>

      </div><!-- /Chart Data body -->
    </div><!-- /Chart Data group -->
  </div><!-- /Chart Data accordion -->

  <!-- Pie & Composition Format accordions (config-driven; own pcFormat state). -->
  <div class="bar-options-accordion" *ngIf="pieCompositionFormatGroups.length">
    <div class="dvm-format-group" *ngFor="let group of pieCompositionFormatGroups" [class.is-open]="isPcFormatGroupOpen(group.name)">
      <button type="button" class="dvm-format-group-head" (click)="togglePcFormatGroup(group.name)">
        <span>{{ group.name }}</span>
        <mat-icon>{{ isPcFormatGroupOpen(group.name) ? 'expand_less' : 'expand_more' }}</mat-icon>
      </button>
      <div class="dvm-format-group-body" *ngIf="isPcFormatGroupOpen(group.name)">
        <ng-container *ngFor="let control of group.controls">
        <div class="dvm-format-row" [class.dvm-format-row-half]="control.width === 'half'" *ngIf="host.formatControlVisible(control, pcFormat)">
          <label>{{ control.label }}</label>
          <input *ngIf="control.control === 'text'" type="text" [ngModel]="pcFormat[control.key]" (ngModelChange)="pcFormat[control.key] = $event; onPieCompositionFormatChange()" [placeholder]="control.placeholder || control.label" />
          <input *ngIf="control.control === 'number'" type="number" [min]="control.min ?? null" [max]="control.max ?? null" [step]="control.step ?? 1" [ngModel]="pcFormat[control.key]" (ngModelChange)="pcFormat[control.key] = $event; onPieCompositionFormatChange()" />
          <input *ngIf="control.control === 'color'" type="color" [ngModel]="pcFormat[control.key]" (ngModelChange)="pcFormat[control.key] = $event; onPieCompositionFormatChange()" />
          <label *ngIf="control.control === 'toggle'" class="dvm-format-toggle">
            <input type="checkbox" [ngModel]="pcFormat[control.key]" (ngModelChange)="pcFormat[control.key] = $event; onPieCompositionFormatChange()" />
          </label>
          <ng-select *ngIf="control.control === 'select'" class="ss-select" appendTo="body" ngClass="ss-dropdown-panel" [items]="host.formatControlOptions(control)" bindLabel="label" bindValue="value" [clearable]="true" [ngModel]="pcFormat[control.key]" (ngModelChange)="pcFormat[control.key] = $event ?? ''; onPieCompositionFormatChange()"></ng-select>
        </div>
        </ng-container>
      </div>
    </div>
  </div>
</div>
